import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, MessageCircle, Mail, MapPin } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { gaEvent } from "../lib/analytics";
import { useT } from "../i18n/LanguageContext";

const BILL_RANGES = [
  "Less than ₹50,000 / month",
  "₹50,000 – ₹1,00,000 / month",
  "₹1,00,000 – ₹3,00,000 / month",
  "₹3,00,000 – ₹10,00,000 / month",
  "More than ₹10,00,000 / month",
];

const SITE_TYPES = [
  "Public charging site",
  "Fleet or logistics depot",
  "Electric bus depot",
  "Commercial or workplace charging",
  "Planned charging site",
  "Other",
];

const CHARGER_COUNTS = ["1–5", "6–20", "21–50", "More than 50", "Planned or not yet known"];

export default function Contact() {
  const t = useT();
  const [form, setForm] = useState({ name: "", company: "", phone: "", email: "", bill_range: "", message: "", site_type: "", charger_count: "" });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const location = useLocation();
  const isChargeHub = new URLSearchParams(location.search).get("topic") === "chargehub";

  useEffect(() => {
    const topic = new URLSearchParams(location.search).get("topic");
    if (topic === "chargehub") {
      setForm((f) => (f.message ? f : { ...f, message: "WattZap ChargeHub Pilot Partnership — " }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    if (!form.name || !form.phone) {
      setError("Please add your name and mobile number.");
      return;
    }
    if (!consent) {
      setError(t("Please accept the privacy consent to continue."));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, consent: true, source: "contact" });
      gaEvent("lead_submitted", { source: "contact" });
      setSubmitted(true);
      toast.success("Thanks — we'll reach out within one business day.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Something went wrong. Please try WhatsApp.";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <section className="bg-[#071A2D] text-white pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[#00D4FF] mb-3">{t("Contact")}</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl max-w-3xl">{t("Book a free EnergyScan.")}</h1>
          <p className="mt-5 text-white/75 max-w-2xl">{t("Share a few details and one of our engineers will reach out — usually within one business day.")}</p>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-3">
            <div className="rounded-2xl border border-slate-100 shadow-card-soft p-7 lg:p-9 bg-white">
              {!submitted ? (
                <form onSubmit={onSubmit} className="grid sm:grid-cols-2 gap-5">
                  <label className="block sm:col-span-1">
                    <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">{t("Name *")}</span>
                    <input required data-testid="contact-name" value={form.name} onChange={update("name")}
                      className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]" />
                  </label>
                  <label className="block sm:col-span-1">
                    <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">{t("Company")}</span>
                    <input data-testid="contact-company" value={form.company} onChange={update("company")}
                      className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]" />
                  </label>
                  <label className="block sm:col-span-1">
                    <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">{t("Mobile * (10-digit)")}</span>
                    <input required data-testid="contact-phone" value={form.phone} onChange={update("phone")}
                      className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]" placeholder="98XXXXXXXX" />
                  </label>
                  <label className="block sm:col-span-1">
                    <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">{t("Email")}</span>
                    <input type="email" data-testid="contact-email" value={form.email} onChange={update("email")}
                      className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]" />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">{t("Monthly bill range")}</span>
                    <select data-testid="contact-bill-range" value={form.bill_range} onChange={update("bill_range")}
                      className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#00D4FF] bg-white">
                      <option value="">{t("Select a range")}</option>
                      {BILL_RANGES.map((r) => <option key={r} value={r}>{t(r)}</option>)}
                    </select>
                  </label>
                  {isChargeHub && (
                    <>
                      <label className="block sm:col-span-1">
                        <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">{t("Site type (optional)")}</span>
                        <select data-testid="contact-site-type" value={form.site_type} onChange={update("site_type")}
                          className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#00D4FF] bg-white">
                          <option value="">{t("Select (optional)")}</option>
                          {SITE_TYPES.map((s) => <option key={s} value={s}>{t(s)}</option>)}
                        </select>
                      </label>
                      <label className="block sm:col-span-1">
                        <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">{t("Number of chargers (optional)")}</span>
                        <select data-testid="contact-charger-count" value={form.charger_count} onChange={update("charger_count")}
                          className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#00D4FF] bg-white">
                          <option value="">{t("Select (optional)")}</option>
                          {CHARGER_COUNTS.map((c) => <option key={c} value={c}>{t(c)}</option>)}
                        </select>
                      </label>
                    </>
                  )}
                  <label className="block sm:col-span-2">
                    <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">{t("How can we help?")}</span>
                    <textarea rows={4} data-testid="contact-message" value={form.message} onChange={update("message")}
                      className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                      placeholder={t("Tell us about your facility and what you'd like to solve.")} />
                  </label>
                  {error && <div data-testid="contact-error" className="sm:col-span-2 text-sm text-red-600">{error}</div>}
                  <label className="sm:col-span-2 flex items-start gap-2.5 text-xs text-slate-600 leading-relaxed cursor-pointer">
                    <input type="checkbox" data-testid="contact-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 accent-[#00D4FF]" />
                    <span>
                      {t("I consent to WattZap Energy Solutions Pvt. Ltd. collecting and using the above details to contact me about my energy-savings enquiry via phone, WhatsApp or email, as described in the")}{" "}
                      <Link to="/privacy" className="text-[#0092b0] underline" data-testid="contact-consent-privacy-link">{t("Privacy Policy")}</Link>.
                    </span>
                  </label>
                  <button type="submit" disabled={submitting} data-testid="contact-submit"
                    className="sm:col-span-2 btn-gradient inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm disabled:opacity-60">
                    {submitting ? t("Sending...") : t("Book my free EnergyScan")}
                  </button>
                </form>
              ) : (
                <div data-testid="contact-success" className="text-center py-8">
                  <div className="w-14 h-14 rounded-full bg-[#21F59B]/15 mx-auto flex items-center justify-center">
                    <CheckCircle2 className="text-[#21F59B]" size={28} />
                  </div>
                  <h3 className="font-heading text-2xl text-graphite mt-5">{t("Thanks — you're on our list.")}</h3>
                  <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto">{t("One of our engineers will reach out within one business day. Prefer a faster reply? Ping us on WhatsApp.")}</p>
                  <a href="https://wa.me/918639602606" target="_blank" rel="noreferrer"
                     className="mt-6 inline-flex items-center gap-2 bg-[#21F59B] text-[#071A2D] px-5 py-3 rounded-full font-medium text-sm">
                    <MessageCircle size={16} /> {t("WhatsApp us")}
                  </a>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="rounded-2xl bg-[#071A2D] text-white p-7 lg:p-9 border border-white/10">
              <h3 className="font-heading text-xl">{t("Prefer a direct line?")}</h3>
              <ul className="mt-5 space-y-4 text-sm text-white/80">
                <li className="flex items-start gap-3"><Mail size={16} className="mt-1 text-[#00D4FF]" /> info@wattzap.in</li>
                <li className="flex items-start gap-3"><MessageCircle size={16} className="mt-1 text-[#21F59B]" /> {t("WhatsApp: +91 86396 02606")}</li>
                <li className="flex items-start gap-3"><MapPin size={16} className="mt-1 text-[#FFC857]" /> {t("Hyderabad, Telangana, India")}</li>
              </ul>
              <div className="mt-8 text-xs text-white/50 leading-relaxed">
                {t("We reply to every enquiry — usually within one business day.")}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
